/**
 * Shared default theme for the public embed and the admin live preview.
 *
 * Not wrapped in @layer: Claritick’s own Bootstrap/Reboot styles are unlayered, and
 * layered rules always lose to unlayered rules for the same property — which stripped
 * all defaults in the in-app preview. Custom CSS still wins when injected after this
 * block (same specificity → cascade order; use !important only if the host page
 * forces a property).
 */

function parseHexColor(hex) {
    if (typeof hex !== "string") return null;
    let s = hex.trim();
    if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
        s = `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(s)) return null;
    return {
        r: parseInt(s.slice(1, 3), 16),
        g: parseInt(s.slice(3, 5), 16),
        b: parseInt(s.slice(5, 7), 16),
    };
}

function relativeLuminance(rgb) {
    const lin = [rgb.r, rgb.g, rgb.b].map((c) => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/**
 * Borders and field fills from card luminance — airy light UIs, soft glass on dark cards.
 *
 * @param {string} textColorHex
 * @param {string} backgroundHex card background from settings
 * @return {{ border: string, inputBg: string }}
 */
export function embedFieldChrome(_textColorHex, backgroundHex) {
    const bgRgb = parseHexColor(backgroundHex);
    const bgLum = bgRgb ? relativeLuminance(bgRgb) : 0.98;
    const isLightCard = bgLum > 0.45;

    if (isLightCard) {
        return {
            border: "rgba(15, 23, 42, 0.10)",
            inputBg: "#f3f5f8",
        };
    }
    return {
        border: "rgba(255, 255, 255, 0.14)",
        inputBg: "rgba(255, 255, 255, 0.06)",
    };
}

/** Default embed copy color — matches Bootstrap body text / app modals. */
export const EMBED_DEFAULT_TEXT_COLOR = "#212529";

/** Default success message color (not green). */
export const EMBED_DEFAULT_SUCCESS_MESSAGE_COLOR = "#212529";

/** Success screen contact link (email, phone, URL) — standard link blue. */
export const EMBED_SUCCESS_CONTACT_LINK_COLOR = "#0d6efd";
export const EMBED_SUCCESS_CONTACT_LINK_HOVER_COLOR = "#0a58ca";
export const EMBED_SUCCESS_CONTACT_FONT_SIZE = "13px";

/** Claritick primary — default embed submit / accent when no custom button color. */
export const EMBED_DEFAULT_BUTTON_COLOR = "#18aee3";

/** Hover for default embed submit (matches admin Bootstrap primary). */
export const EMBED_DEFAULT_BUTTON_HOVER_COLOR = "#1a8fb8";

/** @param {string|null|undefined} buttonColor */
export function resolveEmbedButtonBg(buttonColor) {
    if (buttonColor != null && String(buttonColor).trim() !== "") {
        return String(buttonColor).trim();
    }
    return EMBED_DEFAULT_BUTTON_COLOR;
}

/** @param {string|null|undefined} buttonColor */
export function hasCustomEmbedButtonColor(buttonColor) {
    return buttonColor != null && String(buttonColor).trim() !== "";
}

/**
 * Background for the floating launcher button. Uses the dedicated launcher color
 * when set, otherwise falls back to the form's submit button color.
 * @param {string|null|undefined} launcherColor
 * @param {string|null|undefined} buttonColor
 */
export function resolveEmbedLauncherBg(launcherColor, buttonColor) {
    if (launcherColor != null && String(launcherColor).trim() !== "") {
        return String(launcherColor).trim();
    }
    return resolveEmbedButtonBg(buttonColor);
}

/** Bootstrap tokens — match admin .form-control / .btn; fallbacks for external embed hosts. */
export const EMBED_UI_RADIUS = "var(--bs-border-radius, 0.375rem)";
export const EMBED_UI_RADIUS_LG = "var(--bs-border-radius-lg, 0.5rem)";
export const EMBED_UI_RADIUS_SM = "var(--bs-border-radius-sm, 0.25rem)";

/** Matches Bootstrap ~0.5rem when `form_border_radius` is unset in the database. */
export const EMBED_DEFAULT_FORM_BORDER_RADIUS_PX = 8;

/**
 * @param {number|string|null|undefined} value
 * @returns {number|null} 0–64 px, or null to use theme CSS variables
 */
export function normalizeEmbedFormBorderRadiusPx(value) {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const n = parseInt(String(value), 10);
    if (!Number.isFinite(n)) {
        return null;
    }
    return Math.min(64, Math.max(0, n));
}

/**
 * Card-only corner radius for `.claritick-ticket-embed-card` (inputs keep default radius).
 *
 * @param {number|string|null|undefined} formBorderRadiusPx
 * @returns {string}
 */
export function resolveEmbedCardBorderRadius(formBorderRadiusPx) {
    const px = normalizeEmbedFormBorderRadiusPx(formBorderRadiusPx);
    if (px === null) {
        return EMBED_UI_RADIUS_LG;
    }
    return `${px}px`;
}

/** @param {string} [align] @returns {'left'|'center'|'right'} */
export function normalizeEmbedAlign(align) {
    return align === "center" || align === "right" ? align : "left";
}

/** @param {string} [layout] @returns {'vertical'|'inline'} */
export function normalizeEmbedOptionsLayout(layout) {
    return layout === "inline" ? "inline" : "vertical";
}

/** @param {string} baseClass e.g. claritick-ticket-embed-radio-pills */
export function embedChoiceOptionsLayoutClass(baseClass, layout) {
    const mod = normalizeEmbedOptionsLayout(layout);
    return `${baseClass} ${baseClass}--${mod}`;
}

/** @type {{ value: string, label: string }[]} */
export const EMBED_SUCCESS_FONT_FAMILY_OPTIONS = [
    { value: "poppins", label: "Poppins" },
    { value: "system", label: "System UI" },
    { value: "georgia", label: "Georgia" },
    { value: "arial", label: "Arial" },
];

/** @type {{ value: string, label: string }[]} */
export const EMBED_SUCCESS_FONT_SIZE_OPTIONS = [
    { value: "1rem", label: "Small (16px)" },
    { value: "1.25rem", label: "Medium (20px)" },
    { value: "1.5rem", label: "Large (24px)" },
];

/** @type {{ value: string, label: string }[]} */
export const EMBED_SUCCESS_FONT_WEIGHT_OPTIONS = [
    { value: "400", label: "Normal" },
    { value: "600", label: "Semibold" },
    { value: "700", label: "Bold" },
];

const EMBED_SUCCESS_FONT_FALLBACK =
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif';

const EMBED_SUCCESS_FONT_FAMILY_CSS = {
    poppins: `"Poppins",${EMBED_SUCCESS_FONT_FALLBACK}`,
    system: EMBED_SUCCESS_FONT_FALLBACK,
    georgia: 'Georgia,"Times New Roman",Times,serif',
    arial: "Arial,Helvetica,sans-serif",
};

/**
 * Poppins needs explicit @font-face per weight on embed hosts (no app.css).
 * Use a dedicated family name per file so 600 and 700 never synthesize from Regular.
 * @type {Record<string, { family: string, file: string, cssWeight: string }>}
 */
const EMBED_POPPINS_SUCCESS_FACES = {
    "400": {
        family: "Poppins",
        file: "Poppins-Regular.ttf",
        cssWeight: "400",
    },
    "600": {
        family: "Poppins Embed Semibold",
        file: "Poppins-SemiBold.ttf",
        cssWeight: "400",
    },
    "700": {
        family: "Poppins Embed Bold",
        file: "Poppins-Bold.ttf",
        cssWeight: "400",
    },
};

/** @param {string|null|undefined} raw @returns {'400'|'600'|'700'} */
export function normalizeEmbedSuccessFontWeight(raw) {
    const w = String(raw ?? "")
        .trim()
        .toLowerCase();
    if (w === "700" || w === "bold") {
        return "700";
    }
    if (w === "400" || w === "normal") {
        return "400";
    }
    return "600";
}

/**
 * @param {string} fontKey
 * @param {'400'|'600'|'700'} weightKey
 * @param {string} [assetOrigin]
 * @returns {{ fontFamily: string, fontWeight: string, fontFaceCss: string }}
 */
export function resolveEmbedSuccessTypography(
    fontKey,
    weightKey,
    assetOrigin = ""
) {
    const k = String(fontKey ?? "")
        .trim()
        .toLowerCase();
    if (k === "poppins") {
        const face =
            EMBED_POPPINS_SUCCESS_FACES[weightKey] ||
            EMBED_POPPINS_SUCCESS_FACES["600"];
        const fontFamily = `"${face.family}",${EMBED_SUCCESS_FONT_FALLBACK}`;
        let fontFaceCss = "";
        const origin = String(assetOrigin ?? "").trim().replace(/\/$/, "");
        if (origin) {
            const url = `${origin}/fonts/${face.file}`;
            fontFaceCss =
                `@font-face{font-family:"${face.family}";src:url("${url}") format("truetype");font-style:normal;font-weight:400;font-display:swap;}`;
        }
        return {
            fontFamily,
            fontWeight: face.cssWeight,
            fontFaceCss,
        };
    }
    return {
        fontFamily: embedSuccessFontFamilyCss(fontKey),
        fontWeight: weightKey,
        fontFaceCss: "",
    };
}

/** @param {string|null|undefined} keyOrCustom */
export function embedSuccessFontFamilyCss(keyOrCustom) {
    const k = String(keyOrCustom ?? "")
        .trim()
        .toLowerCase();
    if (EMBED_SUCCESS_FONT_FAMILY_CSS[k]) {
        return EMBED_SUCCESS_FONT_FAMILY_CSS[k];
    }
    if (k) {
        return String(keyOrCustom).trim();
    }
    return EMBED_SUCCESS_FONT_FAMILY_CSS.poppins;
}

/**
 * @param {Record<string, unknown>|null|undefined} cfg
 * @param {string} [assetOrigin] Claritick origin for @font-face (embed script host)
 * @returns {{ color: string, fontFamily: string, fontSize: string, fontWeight: string, fontFaceCss: string }}
 */
export function resolveSuccessThemeFromCfg(cfg, assetOrigin = "") {
    const weightKey = normalizeEmbedSuccessFontWeight(
        cfg?.success_message_font_weight
    );
    const typography = resolveEmbedSuccessTypography(
        cfg?.success_message_font_family,
        weightKey,
        assetOrigin
    );
    return {
        color:
            String(cfg?.success_message_color ?? "").trim() ||
            EMBED_DEFAULT_SUCCESS_MESSAGE_COLOR,
        fontFamily: typography.fontFamily,
        fontSize:
            String(cfg?.success_message_font_size ?? "").trim() || "1.25rem",
        fontWeight: typography.fontWeight,
        fontFaceCss: typography.fontFaceCss,
    };
}

/**
 * @param {string} scopeSel e.g. [data-claritick-instance="…"]
 * @param {string} maxW
 * @param {string} textColor
 * @param {string} bg
 * @param {{ border: string, inputBg: string }} chrome
 * @param {string} btnBg
 * @param {string} btnFg
 * @param {'left'|'center'|'right'} [labelAlign]
 * @param {string|null|undefined} [titleColorOpt] when set, overrides heading color for `.claritick-ticket-embed-title`
 * @param {string|null|undefined} [subtitleColorOpt] when set, overrides `.claritick-ticket-embed-subtitle` (full opacity)
 * @param {string|null|undefined} [minW] optional CSS min-width on `.claritick-ticket-embed-card` (empty → 0)
 * @param {'left'|'center'|'right'} [titleAlign]
 * @param {'left'|'center'|'right'} [subtitleAlign]
 * @param {{ color: string, fontFamily: string, fontSize: string, fontWeight: string, fontFaceCss?: string }|null} [successTheme]
 * @param {string|null|undefined} [configuredButtonColor] retained for call-site arg order; submit now always uses concrete colors
 * @param {number|string|null|undefined} [formBorderRadiusPx] card corner radius in px (0–64)
 * @param {{ keepSuccessCardChrome?: boolean }|null|undefined} [themeOptions]
 */
export function buildEmbedThemeCss(
    scopeSel,
    maxW,
    textColor,
    bg,
    chrome,
    btnBg,
    btnFg,
    labelAlign = "left",
    titleColorOpt = null,
    subtitleColorOpt = null,
    minW = null,
    titleAlign = "left",
    subtitleAlign = "left",
    successTheme = null,
    configuredButtonColor = null,
    formBorderRadiusPx = null,
    themeOptions = null
) {
    const keepSuccessCardChrome = themeOptions?.keepSuccessCardChrome === true;
    const cardRadius = resolveEmbedCardBorderRadius(formBorderRadiusPx);
    const success = successTheme || resolveSuccessThemeFromCfg(null);
    const la = normalizeEmbedAlign(labelAlign);
    const ta = normalizeEmbedAlign(titleAlign);
    const sa = normalizeEmbedAlign(subtitleAlign);
    const titleCol =
        titleColorOpt != null && String(titleColorOpt).trim() !== ""
            ? String(titleColorOpt).trim()
            : textColor;
    const subTrim =
        subtitleColorOpt != null && String(subtitleColorOpt).trim() !== ""
            ? String(subtitleColorOpt).trim()
            : "";
    const subCol = subTrim || textColor;
    const subOpacity = subTrim ? "1" : "0.75";
    const btnRgb = parseHexColor(btnBg);
    const selBg = btnRgb
        ? `rgba(${btnRgb.r},${btnRgb.g},${btnRgb.b},0.22)`
        : "rgba(24, 174, 227, 0.14)";
    const focusRing = btnRgb
        ? `rgba(${btnRgb.r},${btnRgb.g},${btnRgb.b},0.25)`
        : "rgba(24, 174, 227, 0.25)";
    const cardShadow = "none";
    const btnShadow = "none";
    // Always paint the submit button with concrete colors so the configured
    // fill (button_color) and label color (button_text_color) always apply —
    // on the admin preview AND on external sites that don't load Bootstrap.
    // btnBg already falls back to EMBED_DEFAULT_BUTTON_COLOR when no custom
    // fill is set, and btnFg falls back to an auto-contrast (or default white).
    const submitBg = btnBg;
    const submitBorder = btnBg;
    const submitFg = btnFg;
    const submitHoverBg = btnBg;
    const submitHoverBorder = btnBg;
    const submitHoverFg = btnFg;

    /** Max 100%: root fills the embed slot. Otherwise shrink-wrap to the card (no dead horizontal space). */
    const maxWTrim = String(maxW ?? "").trim();
    const rootFillsHost = maxWTrim === "100%";
    const minWTrim = String(minW ?? "").trim();
    const cardMinW = minWTrim ? clampEmbedLengthToParent(minWTrim) : "0";
    const cardMaxW =
        maxWTrim && maxWTrim !== "100%" && !/%$/.test(maxWTrim)
            ? clampEmbedLengthToParent(maxWTrim)
            : maxW;

    /**
     * Block + width 100% so card `min(..., 100%)` resolves against the host slot, not shrink-to-fit.
     * (Inline-flex + a px min-width forced horizontal scroll on narrow viewports.)
     */
    const rootBox = rootFillsHost
        ? "display:block;width:100%;min-height:100%;box-sizing:border-box;overflow:visible;"
        : "display:block;width:100%;max-width:100%;box-sizing:border-box;overflow:visible;";

    return (
        (success.fontFaceCss || "") +
        scopeSel +
        "{" +
        rootBox +
        "}" +
        scopeSel +
        " :where(.claritick-ticket-embed-card){box-sizing:border-box;width:100%;min-width:" +
        cardMinW +
        ";max-width:" +
        cardMaxW +
        ";padding:20px 22px;border-radius:" +
        cardRadius +
        ";border:1px solid " +
        chrome.border +
        ";background:" +
        bg +
        ";box-shadow:" +
        cardShadow +
        ';font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:15px;line-height:1.45;color:' +
        textColor +
        ";-webkit-font-smoothing:antialiased;overflow:visible;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-stage){overflow:visible;}" +
        scopeSel +
        (keepSuccessCardChrome
            ? " :where(.claritick-ticket-embed-card.is-success-state){background:" +
              bg +
              ";border:1px solid " +
              chrome.border +
              ";box-shadow:" +
              cardShadow +
              ";border-radius:" +
              cardRadius +
              ";padding:20px 22px;}"
            : " :where(.claritick-ticket-embed-card.is-success-state){background:transparent;border:none;box-shadow:none;border-radius:0;padding:0;}") +
        scopeSel +
        " :where(.claritick-ticket-embed-card.is-success-state .claritick-ticket-embed-stage){min-height:var(--embed-success-stage-min-h,280px);}" +
        scopeSel +
        " :where(.claritick-ticket-embed-card.is-success-state .claritick-ticket-embed-stage__body){display:flex;flex:1 1 auto;align-items:center;justify-content:center;min-height:0;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-card.is-success-state .claritick-ticket-embed-stage__success){min-height:0;width:100%;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-card.is-success-compact .claritick-ticket-embed-stage){min-height:0;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-card.is-success-compact .claritick-ticket-embed-stage__success){min-height:0;padding:20px 16px 16px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-title){margin:0 0 6px;font-size:1.25rem;font-weight:600;line-height:1.25;letter-spacing:-0.02em;text-align:" +
        ta +
        ";color:" +
        titleCol +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-subtitle){margin:0 0 18px;opacity:" +
        subOpacity +
        ";color:" +
        subCol +
        ";font-size:0.9375rem;line-height:1.4;text-align:" +
        sa +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-form){display:flex;flex-direction:column;gap:14px;margin:0;padding:0;min-width:0;width:100%;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-label){display:flex;flex-direction:column;gap:6px;color:" +
        textColor +
        ";min-width:0;max-width:100%;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-label) > span:first-child{font-weight:500;font-size:0.8125rem;letter-spacing:0.02em;opacity:0.88;text-align:" +
        la +
        ";color:" +
        textColor +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-required-star){color:#c62828;font-weight:600;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-field){padding:10px 12px;border-radius:" +
        EMBED_UI_RADIUS +
        ";border:1px solid " +
        chrome.border +
        ";background:" +
        chrome.inputBg +
        ";color:" +
        textColor +
        ";box-sizing:border-box;transition:border-color .15s ease,box-shadow .15s ease,background-color .15s ease;min-width:0;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-field):not(.claritick-ticket-embed-field--checkbox):not(.claritick-ticket-embed-field--radio){width:100%;max-width:100%;overflow-wrap:break-word;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-field--textarea){resize:vertical;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-birthday-wrap){display:flex;align-items:stretch;width:100%;max-width:100%;position:relative;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-birthday-wrap .claritick-ticket-embed-field--birthday){flex:1;min-width:0;border-top-right-radius:0;border-bottom-right-radius:0;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-birthday-picker-btn){display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;padding:0 12px;border:1px solid " +
        chrome.border +
        ";border-left:none;border-radius:0 " +
        EMBED_UI_RADIUS +
        " " +
        EMBED_UI_RADIUS +
        " 0;background:" +
        chrome.inputBg +
        ";color:" +
        textColor +
        ";cursor:pointer;transition:background .15s ease,border-color .15s ease;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-birthday-picker-btn:hover){background:" +
        chrome.inputBg +
        ";filter:brightness(0.97);}" +
        scopeSel +
        " :where(.claritick-ticket-embed-birthday-picker-btn:focus-visible){outline:2px solid " +
        btnBg +
        ";outline-offset:2px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-birthday-picker-native){position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;opacity:0;pointer-events:none;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-field):not(.claritick-ticket-embed-field--radio):not(.claritick-ticket-embed-field--checkbox):focus{outline:none;border-color:" +
        btnBg +
        ";box-shadow:0 0 0 3px " +
        focusRing +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-field):not(.claritick-ticket-embed-field--radio):not(.claritick-ticket-embed-field--checkbox):focus-visible{outline:none;border-color:" +
        btnBg +
        ";box-shadow:0 0 0 3px " +
        focusRing +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-field)::placeholder{color:inherit;opacity:0.42;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-option-row--checkbox){display:flex;align-items:center;gap:10px;justify-content:flex-start;color:" +
        textColor +
        ";font-size:0.9375rem;min-width:0;max-width:100%;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-checkbox-list){display:flex;flex-direction:column;align-items:flex-start;gap:8px;min-width:0;max-width:100%;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-checkbox-list--inline){flex-direction:row;flex-wrap:wrap;align-items:center;gap:12px 16px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-radio-pills){display:inline-flex;flex-direction:column;align-items:stretch;gap:8px;width:max-content;max-width:100%;min-width:0;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-radio-pills--inline){display:flex;flex-direction:row;flex-wrap:wrap;align-items:center;width:auto;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-radio-pills--inline .claritick-ticket-embed-radio-pill){width:auto;max-width:100%;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-radio-pills--vertical .claritick-ticket-embed-radio-pill){width:100%;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-option-group--radio .claritick-ticket-embed-radio-pills--vertical){align-self:flex-start;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-radio-pill){display:inline-flex;align-items:center;gap:6px;margin:0;padding:9px 15px;border:1px solid " +
        chrome.border +
        ";border-radius:" +
        EMBED_UI_RADIUS +
        ";cursor:pointer;background:" +
        chrome.inputBg +
        ";color:" +
        textColor +
        ";max-width:100%;box-sizing:border-box;transition:border-color .15s ease,background-color .15s ease,box-shadow .15s ease;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-radio-pill span){overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:" +
        textColor +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-radio-pill:has(input:checked)){background:" +
        selBg +
        ";border-color:" +
        btnBg +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-field--checkbox){margin:0;flex-shrink:0;outline:none;box-shadow:none;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-field--radio){margin:0;flex-shrink:0;outline:none;box-shadow:none;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-option-group--radio){display:flex;flex-direction:column;gap:6px;min-width:0;max-width:100%;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-option-group--radio > span:first-child){font-weight:500;font-size:0.8125rem;letter-spacing:0.02em;opacity:0.88;text-align:" +
        la +
        ";color:" +
        textColor +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-option-group--checkbox){display:flex;flex-direction:column;gap:6px;min-width:0;max-width:100%;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-option-group--checkbox > span:first-child){font-weight:500;font-size:0.8125rem;letter-spacing:0.02em;opacity:0.88;text-align:" +
        la +
        ";color:" +
        textColor +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-submit){display:inline-flex;align-items:center;justify-content:center;gap:8px;margin-top:2px;padding:11px 18px;border-radius:" +
        EMBED_UI_RADIUS +
        ";border:1px solid " +
        submitBorder +
        ";cursor:pointer;font-weight:600;font-size:0.875rem;letter-spacing:0.01em;background:" +
        submitBg +
        ";color:" +
        submitFg +
        ";box-shadow:" +
        btnShadow +
        ";transition:background-color .12s ease,border-color .12s ease,color .12s ease,transform .05s ease;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-submit__spinner){display:none;width:1em;height:1em;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:claritick-embed-spin .7s linear infinite;flex-shrink:0;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-submit.is-loading .claritick-ticket-embed-submit__spinner){display:inline-block;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-submit):hover{background:" +
        submitHoverBg +
        ";border-color:" +
        submitHoverBorder +
        ";color:" +
        submitHoverFg +
        ";filter:brightness(1.03);" +
        "}" +
        scopeSel +
        " :where(.claritick-ticket-embed-submit):active{transform:translateY(1px);filter:brightness(0.98);}" +
        scopeSel +
        " :where(.claritick-ticket-embed-submit):disabled,.claritick-ticket-embed-submit.is-loading{opacity:0.85;cursor:wait;transform:none;filter:none;box-shadow:none;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-submit.is-loading:hover){filter:none;}" +
        "@keyframes claritick-embed-spin{to{transform:rotate(360deg);}}" +
        scopeSel +
        " :where(.claritick-ticket-embed-status){font-size:13px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-status:not(:empty)){min-height:20px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-status:empty){display:none;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-status--error){color:#b00020;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-status--success){color:" +
        EMBED_DEFAULT_SUCCESS_MESSAGE_COLOR +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-stage){display:flex;flex-direction:column;width:100%;min-width:0;box-sizing:border-box;position:relative;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-stage__header){width:100%;max-width:100%;min-width:0;box-sizing:border-box;flex-shrink:0;padding:0;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-card.is-success-state .claritick-ticket-embed-stage__header){display:none;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-card.is-success-state .claritick-ticket-embed-title)," +
        scopeSel +
        " :where(.claritick-ticket-embed-card.is-success-state .claritick-ticket-embed-subtitle){display:none;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-success-disclaimer:empty){display:none;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-success-disclaimer){margin:0 0 12px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-stage__body){display:grid;flex:1 1 auto;width:100%;min-width:0;min-height:0;box-sizing:border-box;position:relative;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-stage__form)," +
        scopeSel +
        " :where(.claritick-ticket-embed-stage__success){grid-area:1/1;width:100%;max-width:100%;min-width:0;box-sizing:border-box;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-card[data-width-locked]){flex-shrink:0;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-stage__form){visibility:visible;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-stage__form.is-exiting)," +
        scopeSel +
        " :where(.claritick-ticket-embed-stage__form.is-concealed){visibility:hidden;pointer-events:none;position:absolute;inset:0;width:100%;height:0;overflow:hidden;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-stage__success){display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;gap:8px;visibility:hidden;pointer-events:none;padding:16px 12px;min-height:120px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-stage__success.is-visible){visibility:visible;pointer-events:auto;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-success-message){margin:0;max-width:26em;font-family:" +
        success.fontFamily +
        ";font-size:" +
        success.fontSize +
        ";font-weight:" +
        success.fontWeight +
        ";line-height:1.35;letter-spacing:-0.02em;color:" +
        success.color +
        ";font-synthesis:none;-webkit-font-smoothing:antialiased;white-space:pre-wrap;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-success-message a){color:inherit;text-decoration:underline;text-underline-offset:2px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-success-message a:hover){opacity:0.9;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-success-contact){margin:0;max-width:26em;font-size:" +
        EMBED_SUCCESS_CONTACT_FONT_SIZE +
        ";line-height:1.45;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-success-contact a){color:" +
        EMBED_SUCCESS_CONTACT_LINK_COLOR +
        ";text-decoration:underline;text-underline-offset:2px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-success-contact a:hover){color:" +
        EMBED_SUCCESS_CONTACT_LINK_HOVER_COLOR +
        ";}" +
        scopeSel +
        " :where(.claritick-ticket-embed-success-disclaimer){margin:0;max-width:26em;font-size:16px;line-height:1.45;color:" +
        success.color +
        ";opacity:0.88;white-space:pre-wrap;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-success-checkmark,.claritick-ticket-embed-success-checklist){display:block;width:88px;height:88px;max-width:100%;flex-shrink:0;border:none;pointer-events:none;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-track-btn){display:inline-flex;align-items:center;justify-content:center;margin-top:4px;padding:10px 16px;border-radius:" +
        EMBED_UI_RADIUS +
        ";border:none;font-weight:600;font-size:0.875rem;letter-spacing:0.01em;text-decoration:none;background:" +
        btnBg +
        ";color:" +
        btnFg +
        ";box-shadow:" +
        btnShadow +
        ";cursor:pointer;transition:filter .15s ease,transform .15s ease;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-track-btn:hover){filter:brightness(1.03);}" +
        scopeSel +
        " :where(.claritick-ticket-embed-track-btn:active){transform:translateY(1px);filter:brightness(0.98);}" +
        scopeSel +
        " :where(.claritick-ticket-embed-track-btn--preview){cursor:default;pointer-events:none;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-track-btn--preview:hover)," +
        scopeSel +
        " :where(.claritick-ticket-embed-track-btn--preview:active){filter:none;transform:none;}"
    );
}

/** Success checkmark shown after embed submit (plays once, does not loop). */
export const EMBED_SUCCESS_CHECKMARK_SRC = "/images/Checkmark-Success.svg";

/**
 * Pause SMIL just before the 2s teardown (check drawn, no fade/reset).
 * @type {number}
 */
export const EMBED_SUCCESS_CHECKMARK_FREEZE_S = 1.58;

/** @type {number} */
export const EMBED_SUCCESS_CHECKMARK_FREEZE_MS = Math.round(
    EMBED_SUCCESS_CHECKMARK_FREEZE_S * 1000
);

/** @deprecated Use EMBED_SUCCESS_CHECKMARK_SRC */
export const EMBED_SUCCESS_CHECKLIST_SRC = EMBED_SUCCESS_CHECKMARK_SRC;

/** @deprecated Looping checklist removed; same asset as checkmark. */
export const EMBED_SUCCESS_CHECKLIST_LOOP_SRC = EMBED_SUCCESS_CHECKMARK_SRC;

/** @param {boolean} [_loop] ignored — always the single-play checkmark */
export function embedChecklistSrc(_loop = false) {
    return EMBED_SUCCESS_CHECKMARK_SRC;
}

/**
 * Freeze animated SVG at the success checkmark pose (no loop, no end blank).
 * @param {SVGSVGElement|null} root
 */
export function freezeEmbedSuccessCheckmarkSvg(root) {
    if (!root || typeof root.pauseAnimations !== "function") {
        return;
    }
    try {
        if (typeof root.setCurrentTime === "function") {
            root.setCurrentTime(EMBED_SUCCESS_CHECKMARK_FREEZE_S);
        }
        root.pauseAnimations();
    } catch (_e) {
        /* ignore */
    }
}

/**
 * Pin the card to its laid-out width so submit / success swaps do not
 * shrink-wrap wider (e.g. jump to max-width) when content changes.
 */
export function lockEmbedCardWidth(cardEl) {
    if (!cardEl || cardEl.getAttribute("data-width-locked") === "1") {
        return;
    }
    const w = Math.round(cardEl.getBoundingClientRect().width);
    if (w > 0) {
        cardEl.style.width = `${w}px`;
        cardEl.setAttribute("data-width-locked", "1");
    }
}

/** @param {HTMLElement|null} cardEl */
export function unlockEmbedCardWidth(cardEl) {
    if (!cardEl) {
        return;
    }
    cardEl.style.width = "";
    cardEl.removeAttribute("data-width-locked");
}

/** @param {HTMLElement|null} stageEl */
export function pinEmbedStageHeightForSuccess(stageEl) {
    if (!stageEl) {
        return;
    }
    const h = Math.round(stageEl.getBoundingClientRect().height);
    if (h > 0) {
        stageEl.style.minHeight = `${h}px`;
        stageEl.style.setProperty("--embed-success-stage-min-h", `${h}px`);
    }
}

/** @param {HTMLElement|null} stageEl */
export function clearEmbedStageSuccessHeight(stageEl) {
    if (!stageEl) {
        return;
    }
    stageEl.style.minHeight = "";
    stageEl.style.removeProperty("--embed-success-stage-min-h");
}

/**
 * After submit: drop the card chrome so only the success content remains visible.
 * @param {HTMLElement|null} cardEl
 * @param {boolean} isSuccess
 * @param {{ pinStageHeight?: boolean }} [options] When `pinStageHeight` is false (floating widget), the stage shrinks to the success content.
 */
export function setEmbedCardSuccessState(cardEl, isSuccess, options = {}) {
    if (!cardEl) {
        return;
    }
    const stage = cardEl.querySelector(".claritick-ticket-embed-stage");
    const pinStageHeight = options.pinStageHeight !== false;
    if (isSuccess) {
        if (pinStageHeight) {
            pinEmbedStageHeightForSuccess(stage);
        } else {
            clearEmbedStageSuccessHeight(stage);
        }
        unlockEmbedCardWidth(cardEl);
        cardEl.classList.add("is-success-state");
        if (!pinStageHeight) {
            cardEl.classList.add("is-success-compact");
        }
    } else {
        cardEl.classList.remove("is-success-state", "is-success-compact");
        clearEmbedStageSuccessHeight(stage);
    }
}

export const EMBED_DISPLAY_MODE_INLINE = "inline";
export const EMBED_DISPLAY_MODE_FLOATING = "floating";

/** @type {{ value: string, label: string }[]} */
export const EMBED_LAUNCHER_POSITION_OPTIONS = [
    { value: "bottom-right", label: "Bottom right" },
    { value: "bottom-left", label: "Bottom left" },
    { value: "top-right", label: "Top right" },
    { value: "top-left", label: "Top left" },
];

/** @param {string|null|undefined} raw */
export function normalizeEmbedDisplayMode(raw) {
    return String(raw ?? "")
        .trim()
        .toLowerCase() === EMBED_DISPLAY_MODE_FLOATING
        ? EMBED_DISPLAY_MODE_FLOATING
        : EMBED_DISPLAY_MODE_INLINE;
}

/** @param {string|null|undefined} raw @returns {'top-right'|'top-left'|'bottom-right'|'bottom-left'} */
export function normalizeEmbedLauncherPosition(raw) {
    const p = String(raw ?? "")
        .trim()
        .toLowerCase();
    if (
        p === "top-right" ||
        p === "top-left" ||
        p === "bottom-left" ||
        p === "bottom-right"
    ) {
        return p;
    }
    return "bottom-right";
}

/** @see public/images/form-launcher.svg */
export const EMBED_LAUNCHER_ICON_SRC = "/images/form-launcher.svg";

const EMBED_FLOATING_PANEL_VIEWPORT_PAD_PX = 40;
const EMBED_FLOATING_PANEL_DEFAULT_PX = 420;

/** Max height for the floating popup (viewport minus corner inset + launcher). */
export function embedFloatingPanelMaxHeightCss() {
    const launcherPx = 56;
    const gapPx = 12;
    const shellInsetPx = 20;
    const panelOff = launcherPx + gapPx;

    return `calc(100vh - ${shellInsetPx * 2 + panelOff}px)`;
}

/** Apply scroll cap on the live floating panel (inline styles beat CSS on embed script). */
export function applyEmbedFloatingPanelScrollLayout(panelEl, rootEl) {
    if (!panelEl || !rootEl) {
        return;
    }
    panelEl.style.maxHeight = embedFloatingPanelMaxHeightCss();
    panelEl.style.overflow = 'hidden';
    panelEl.style.flexDirection = 'column';
    rootEl.style.flex = '1 1 auto';
    rootEl.style.minHeight = '0';
    rootEl.style.overflowX = 'hidden';
    rootEl.style.overflowY = 'auto';
}

/** Never wider than the embed slot / parent (inline mode). */
function clampEmbedLengthToParent(cssLen) {
    const s = String(cssLen ?? "").trim();
    if (!s || s === "0") {
        return s || "0";
    }
    return `min(${s}, 100%)`;
}

/** @returns {number|null} pixel length for bare numbers / `Npx` only */
export function parseEmbedWidthPx(raw) {
    const s = String(raw ?? "").trim();
    if (!s) {
        return null;
    }
    if (/^\d+(\.\d+)?$/.test(s)) {
        return parseFloat(s);
    }
    const m = /^(\d+(?:\.\d+)?)px$/i.exec(s);
    if (m) {
        return parseFloat(m[1]);
    }
    return null;
}

/** Bare numbers → px; empty → 100%. */
export function normalizeEmbedFormMaxWidth(raw) {
    const s = String(raw ?? "").trim();
    if (!s) {
        return "100%";
    }
    if (/^\d+(\.\d+)?$/.test(s)) {
        return `${s}px`;
    }
    return s;
}

/**
 * Floating panel width from form min/max (avoids clipping when min-width exceeds 420px).
 *
 * @param {string|null|undefined} formMaxWidth
 * @param {string|null|undefined} formMinWidth
 * @returns {{ targetPx: number, minPx: number|null, widthCss: string, minWidthCss: string|null }}
 */
export function resolveEmbedFloatingPanelWidth(formMaxWidth, formMinWidth) {
    const minPx = parseEmbedWidthPx(formMinWidth);
    const maxPx = parseEmbedWidthPx(formMaxWidth);
    const maxNorm = normalizeEmbedFormMaxWidth(formMaxWidth);
    const maxIsFluid = maxNorm === "100%" || /%$/.test(maxNorm);

    let targetPx = EMBED_FLOATING_PANEL_DEFAULT_PX;
    if (!maxIsFluid && maxPx != null) {
        targetPx = maxPx;
    }
    if (minPx != null) {
        targetPx = Math.max(targetPx, minPx);
    }

    const rounded = Math.round(targetPx);
    const viewportCap = `calc(100vw - ${EMBED_FLOATING_PANEL_VIEWPORT_PAD_PX}px)`;
    const widthCss = `min(${viewportCap}, ${rounded}px)`;

    return {
        targetPx: rounded,
        minPx: minPx != null ? Math.round(minPx) : null,
        widthCss,
        /** @deprecated unused — width/max-width clamp; fixed min-width broke mobile viewports */
        minWidthCss: null,
    };
}

/**
 * @param {string} [assetOrigin] Claritick host origin for the SVG URL (embed script host)
 */
export function embedLauncherIconHtml(assetOrigin = "") {
    const origin = String(assetOrigin ?? "").trim().replace(/\/$/, "");
    const src = (origin || "") + EMBED_LAUNCHER_ICON_SRC;
    return (
        '<img class="claritick-ticket-embed-launcher__icon" src="' +
        src +
        '" width="26" height="26" alt="" aria-hidden="true" decoding="async" />'
    );
}

/** Visible hover label for the floating launcher (form title or fallback). */
export function embedLauncherHoverText(cfg) {
    const title = cfg?.title;
    if (title != null && String(title).trim() !== "") {
        return String(title).trim();
    }
    return "Contact us";
}

/**
 * Fixed corner launcher + slide-up panel (chat-widget style).
 * @param {string} scopeSel e.g. [data-claritick-instance="…"]
 * @param {'top-right'|'top-left'|'bottom-right'|'bottom-left'} position
 * @param {string} btnBg
 * @param {string} btnFg
 */
export function buildEmbedFloatingLauncherCss(
    scopeSel,
    position,
    btnBg,
    btnFg,
    formMaxWidth,
    formMinWidth
) {
    const pos = normalizeEmbedLauncherPosition(position);
    const panelSizing = resolveEmbedFloatingPanelWidth(
        formMaxWidth,
        formMinWidth
    );
    const launcherPx = 56;
    const gapPx = 12;
    const panelOff = launcherPx + gapPx;
    const panelMaxHeight = embedFloatingPanelMaxHeightCss();
    const isTop = pos.startsWith("top");
    const isRight = pos.endsWith("right");
    const shellV = isTop ? "top:20px;" : "bottom:20px;";
    const shellH = isRight ? "right:20px;" : "left:20px;";
    const launcherAnchor = isTop
        ? "top:0;" + (isRight ? "right:0;left:auto;bottom:auto;" : "left:0;right:auto;bottom:auto;")
        : "bottom:0;" + (isRight ? "right:0;left:auto;top:auto;" : "left:0;right:auto;top:auto;");
    const panelAnchor = isTop
        ? "top:" +
          panelOff +
          "px;bottom:auto;" +
          (isRight ? "right:0;left:auto;" : "left:0;right:auto;")
        : "bottom:" +
          panelOff +
          "px;top:auto;" +
          (isRight ? "right:0;left:auto;" : "left:0;right:auto;");
    const panelOrigin = isTop
        ? "transform-origin:" + (isRight ? "top right" : "top left") + ";"
        : "transform-origin:" + (isRight ? "bottom right" : "bottom left") + ";";
    const panelSlide = isTop ? "translateY(-8px)" : "translateY(8px)";

    return (
        scopeSel +
        ".claritick-ticket-embed-floating{position:fixed;z-index:2147483646;" +
        shellV +
        shellH +
        "width:" +
        launcherPx +
        "px;height:" +
        launcherPx +
        "px;overflow:visible;pointer-events:none;box-sizing:border-box;}" +
        scopeSel +
        ".claritick-ticket-embed-floating > *{pointer-events:auto;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher){position:absolute;" +
        launcherAnchor +
        "display:inline-flex;align-items:center;justify-content:center;width:" +
        launcherPx +
        "px;height:" +
        launcherPx +
        "px;padding:0;border:none;border-radius:50%;cursor:pointer;background:" +
        btnBg +
        ";color:" +
        btnFg +
        ";box-shadow:0 4px 14px rgba(15,23,42,0.22),0 2px 6px rgba(15,23,42,0.12);transition:transform .2s ease,box-shadow .2s ease,filter .15s ease;z-index:2;overflow:visible;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher:hover){filter:brightness(1.05);transform:scale(1.04);}" +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher:focus-visible){outline:2px solid " +
        btnBg +
        ";outline-offset:3px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher__icon){display:block;width:26px;height:26px;object-fit:contain;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher__tooltip){position:absolute;top:50%;transform:translateY(-50%);white-space:nowrap;padding:6px 10px;border-radius:8px;background:rgba(15,23,42,0.92);color:#fff;font-size:13px;line-height:1.3;font-weight:500;box-shadow:0 4px 12px rgba(15,23,42,0.18);opacity:0;visibility:hidden;pointer-events:none;transition:opacity .15s ease,visibility .15s ease;z-index:3;max-width:min(240px,50vw);overflow:hidden;text-overflow:ellipsis;}" +
        (isRight
            ? scopeSel +
              " :where(.claritick-ticket-embed-launcher__tooltip){right:calc(100% + 10px);left:auto;}"
            : scopeSel +
              " :where(.claritick-ticket-embed-launcher__tooltip){left:calc(100% + 10px);right:auto;}") +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher:hover .claritick-ticket-embed-launcher__tooltip){opacity:1;visibility:visible;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-floating-panel){position:absolute;" +
        panelAnchor +
        "display:none;flex-direction:column;width:" +
        panelSizing.widthCss +
        ";max-width:" +
        panelSizing.widthCss +
        ";max-height:" +
        panelMaxHeight +
        ";overflow:hidden;box-sizing:border-box;" +
        panelOrigin +
        "opacity:0;transform:scale(0.96) " +
        panelSlide +
        ";transition:opacity .22s ease,transform .22s ease;z-index:1;}" +
        scopeSel +
        ".claritick-ticket-embed-floating.is-open :where(.claritick-ticket-embed-floating-panel){display:flex;opacity:1;transform:scale(1) translateY(0);}" +
        scopeSel +
        " :where(.claritick-ticket-embed-floating-panel__header){flex:0 0 auto;display:flex;justify-content:flex-end;align-items:center;padding:6px 6px 0;z-index:3;box-sizing:border-box;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-floating-panel__header .claritick-ticket-embed-floating-close){position:static;top:auto;right:auto;margin:0;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-floating-panel > .claritick-ticket-embed-root){display:block;flex:1 1 auto;min-height:0;overflow-x:hidden;overflow-y:auto;-webkit-overflow-scrolling:touch;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-floating-close){width:32px;height:32px;padding:0;border:none;border-radius:8px;background:rgba(15,23,42,0.06);color:inherit;font-size:1.35rem;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s ease;flex-shrink:0;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-floating-close:hover){background:rgba(15,23,42,0.12);}" +
        scopeSel +
        " :where(.claritick-ticket-embed-floating .claritick-ticket-embed-card.is-success-compact .claritick-ticket-embed-stage__success){padding:16px 16px 16px 16px;box-sizing:border-box;}"
    );
}

/** Editor live preview: launcher + close chrome only (layout handled in embed-form-editor.css). */
export function buildEmbedFloatingLauncherPreviewCss(scopeSel, btnBg, btnFg) {
    const launcherPx = 56;
    return (
        scopeSel +
        " :where(.claritick-ticket-embed-launcher){display:inline-flex;align-items:center;justify-content:center;width:" +
        launcherPx +
        "px;height:" +
        launcherPx +
        "px;padding:0;border:none;border-radius:50%;cursor:pointer;background:" +
        btnBg +
        ";color:" +
        btnFg +
        ";box-shadow:0 4px 14px rgba(15,23,42,0.22),0 2px 6px rgba(15,23,42,0.12);transition:transform .2s ease,box-shadow .2s ease,filter .15s ease;flex-shrink:0;position:relative;overflow:visible;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher:hover){filter:brightness(1.05);transform:scale(1.04);}" +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher:focus-visible){outline:2px solid " +
        btnBg +
        ";outline-offset:3px;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher__icon){display:block;width:26px;height:26px;object-fit:contain;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher__tooltip){position:absolute;top:50%;right:calc(100% + 10px);left:auto;transform:translateY(-50%);white-space:nowrap;padding:6px 10px;border-radius:8px;background:rgba(15,23,42,0.92);color:#fff;font-size:13px;line-height:1.3;font-weight:500;box-shadow:0 4px 12px rgba(15,23,42,0.18);opacity:0;visibility:hidden;pointer-events:none;transition:opacity .15s ease,visibility .15s ease;z-index:3;max-width:min(240px,50vw);overflow:hidden;text-overflow:ellipsis;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-launcher:hover .claritick-ticket-embed-launcher__tooltip){opacity:1;visibility:visible;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-floating-panel){position:relative;box-sizing:border-box;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-floating-close){position:absolute;top:8px;right:8px;z-index:3;width:32px;height:32px;padding:0;border:none;border-radius:8px;background:rgba(15,23,42,0.06);color:inherit;font-size:1.35rem;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s ease;}" +
        scopeSel +
        " :where(.claritick-ticket-embed-floating-close:hover){background:rgba(15,23,42,0.12);}"
    );
}

/**
 * Visitor-facing success copy — stored message only (no auto-append).
 *
 * @param {string|Record<string, unknown>|null|undefined} cfgOrMessage
 * @returns {string}
 */
export function resolveEmbedSuccessMessage(cfgOrMessage) {
    const raw =
        typeof cfgOrMessage === "string"
            ? cfgOrMessage
            : cfgOrMessage?.success_message ?? "";
    const msg = String(raw ?? "").trim();
    return (
        msg ||
        "Thank you for contacting support!"
    );
}

/** Default copy for the success-screen disclaimer (separate from `success_message`). */
export const EMBED_DEFAULT_SUCCESS_DISCLAIMER =
    "Our team will get back to you soon.\nThank you for your patience.";

export function resolveEmbedSuccessDisclaimer(cfg) {
    return String(cfg?.success_disclaimer ?? "").trim();
}

/** @param {Record<string, unknown>|string|null|undefined} cfgOrValue */
export function resolveEmbedSupportContactRaw(cfgOrValue) {
    if (cfgOrValue != null && typeof cfgOrValue === "object") {
        return String(cfgOrValue.support_email ?? "").trim();
    }
    return String(cfgOrValue ?? "").trim();
}

/**
 * Resolve optional success-screen contact (stored in `support_email`) to a link.
 * @param {Record<string, unknown>|string|null|undefined} cfgOrValue
 * @returns {{ href: string, label: string, external: boolean } | null}
 */
export function resolveEmbedSupportContact(cfgOrValue) {
    const value = resolveEmbedSupportContactRaw(cfgOrValue);
    if (!value) {
        return null;
    }

    if (/^mailto:/i.test(value)) {
        const email = value.replace(/^mailto:/i, "").trim();
        return email
            ? { href: `mailto:${email}`, label: email, external: false }
            : null;
    }
    if (/^tel:/i.test(value)) {
        const tel = value.replace(/^tel:/i, "").trim();
        return tel
            ? { href: `tel:${tel}`, label: tel, external: false }
            : null;
    }
    if (/^https?:\/\//i.test(value)) {
        return { href: value, label: value, external: true };
    }

    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return { href: `mailto:${value}`, label: value, external: false };
    }

    const digits = value.replace(/\D/g, "");
    if (digits.length >= 7 && /^[+]?[\d\s().\-]+$/.test(value)) {
        const telHref =
            "tel:" + (value.trimStart().startsWith("+") ? "+" : "") + digits;
        return { href: telHref, label: value, external: false };
    }

    if (/^www\./i.test(value)) {
        return { href: `https://${value}`, label: value, external: true };
    }

    if (/^[^\s@]+\.[^\s@]+$/.test(value) && !value.includes(" ")) {
        return { href: `https://${value}`, label: value, external: true };
    }

    return null;
}

/** @deprecated Use resolveEmbedSupportContactRaw — kept for call-site clarity */
export function resolveEmbedSupportEmail(cfg) {
    return resolveEmbedSupportContactRaw(cfg);
}
